import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useRedeemCodeMutation } from "../../features/api/bonusApiSlice";
import { apiSlice } from "../../features/api/apiSlice";

const CODE_LENGTH = 6;

const RedeemCodeBox = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const schema = yup.object().shape({
        code: yup
            .string()
            .required(t("promocenter.redeeminvalid"))
            .matches(/^[A-Z0-9]{6}$/, t("promocenter.redeeminvalid")),
    });

    const { control, handleSubmit, reset, setValue } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { code: "" },
    });

    const [redeemCode, { isLoading, isSuccess, isError, error, data }] = useRedeemCodeMutation();

    const submitCode = async (formData) => {
        // .unwrap() would reject on failure; the isError branch below already handles it,
        // so swallow here to avoid an unhandled rejection in the console.
        try {
            await redeemCode({ code: formData.code }).unwrap();
        } catch (err) {
            // handled by the isError effect
        }
    };

    useEffect(() => {
        if (!isLoading && isSuccess) {
            const amount = data?.result?.rewardAmount;
            Swal.fire({
                icon: "success",
                title: t("promocenter.redeemsuccess"),
                // Reward amount shown as the body line when the API returns one.
                text: amount
                    ? `${process.env.REACT_APP_CURRENCY_CODE} ${amount}`
                    : undefined,
                confirmButtonText: "OK",
            });
            reset();
            // Refreshes the header balance and the promo list without a page reload.
            dispatch(apiSlice.util.invalidateTags(["Profile", "BonusListPromoCenter"]));
        }
        if (isError) {
            Swal.fire({
                icon: "error",
                title: error?.data?.message || t("promocenter.redeemfailed"),
                confirmButtonText: "OK",
            });
        }
    }, [isLoading, isSuccess, isError]);

    return (
        <Box
            mt={2}
            sx={{
                // Deliberately no background: a hardcoded panel colour does not respond to
                // the per-tenant theme, and looked like a dark slab on light-themed brands.
                paddingY: { xs: 1, sm: 1.5 },
            }}
        >
            <Typography sx={{ fontWeight: "bold", fontSize: 13, mb: 1 }}>
                {t("promocenter.redeemtitle")}
            </Typography>

            <form onSubmit={handleSubmit(submitCode)}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1,
                        alignItems: { xs: "stretch", sm: "flex-start" },
                    }}
                >
                    <Controller
                        name="code"
                        control={control}
                        render={({ field, fieldState: { error: fieldError } }) => (
                            <TextField
                                {...field}
                                size="small"
                                placeholder={t("promocenter.redeemplaceholder")}
                                error={!!fieldError}
                                helperText={fieldError ? fieldError.message : ""}
                                disabled={isLoading}
                                onChange={(e) =>
                                    setValue("code", e.target.value.toUpperCase(), {
                                        shouldValidate: false,
                                    })
                                }
                                inputProps={{
                                    maxLength: CODE_LENGTH,
                                    style: { textTransform: "uppercase", letterSpacing: 2 },
                                }}
                                sx={{ flex: 1, maxWidth: { sm: 260 } }}
                            />
                        )}
                    />

                    <Button
                        type="submit"
                        disabled={isLoading}
                        sx={{
                           background: process.env.REACT_APP_COLOR_BUTTON_PRIMARY || "linear-gradient(180deg, hsla(352, 82%, 54%, 1) 15%, hsla(352, 56%, 47%, 1) 82%)",
                            borderRadius: 20,
                            textTransform: "none",
                            fontWeight: "bold",
                            color: "white",
                            paddingX: 3,
                            height: 40,
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
                        }}
                    >
                        {t("promocenter.redeembutton")}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default RedeemCodeBox;
